import {
  PublicClientApplication,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";

// Get tenant domains from environment variables
const getTenantConfigs = () => {
  const configs = {};

  const domainConfigs = [
    {
      domain: process.env.REACT_APP_AZURE_1_DOMAIN,
      clientId: process.env.REACT_APP_AZURE_1_CLIENT_ID,
      tenantId: process.env.REACT_APP_AZURE_1_TENANT_ID,
    },
    {
      domain: process.env.REACT_APP_AZURE_2_DOMAIN,
      clientId: process.env.REACT_APP_AZURE_2_CLIENT_ID,
      tenantId: process.env.REACT_APP_AZURE_2_TENANT_ID,
    },
  ];

  domainConfigs.forEach(({ domain, clientId, tenantId }) => {
    if (domain && clientId && tenantId) {
      configs[domain] = { clientId, tenantId };
    }
  });

  return configs;
};

export const getAllowedDomains = () => Object.keys(getTenantConfigs());

const tenantConfigs = getTenantConfigs();
const defaultClientId = process.env.REACT_APP_DEFAULT_CLIENT_ID;
const defaultTenantId = process.env.REACT_APP_DEFAULT_TENANT_ID;

export const clearCache = () => {
  localStorage.clear();
  sessionStorage.clear();
};

const getDomainFromEmail = (email) => {
  if (typeof email === "string" && email.includes("@")) {
    return email.split("@")[1].toLowerCase();
  }
  return null;
};

const createMsalConfig = (email) => {
  const domain = getDomainFromEmail(email);
  const tenantConfig = (domain && tenantConfigs[domain]) || {
    clientId: defaultClientId,
    tenantId: defaultTenantId,
  };
  return {
    auth: {
      clientId: tenantConfig.clientId,
      authority: `https://login.microsoftonline.com/${tenantConfig.tenantId}`,
      redirectUri: window.location.origin,
    },
    cache: {
      cacheLocation: "localStorage",
      storeAuthStateInCookie: false,
    },
  };
};

let msalInstance = null;
export const initMsalOnce = (email) => {
  const msalConfig = createMsalConfig(email);
  const prevConfigStr = localStorage.getItem("msalConfig");
  let shouldInit = true;

  if (prevConfigStr) {
    try {
      const prevConfig = JSON.parse(prevConfigStr);
      if (
        prevConfig.auth &&
        prevConfig.auth.clientId === msalConfig.auth.clientId &&
        prevConfig.auth.authority === msalConfig.auth.authority
      ) {
        shouldInit = false;
      }
    } catch {
      // ignore parse error, will re-init
    }
  }

  if (shouldInit || !msalInstance) {
    localStorage.setItem("msalConfig", JSON.stringify(msalConfig));
    msalInstance = new PublicClientApplication(msalConfig);
  }
  return msalInstance;
};
export const getLoginRequest = () => {
  const configStr = localStorage.getItem("msalConfig");
  if (!configStr) {
    return {
      scopes: [`${defaultClientId}/.default`],
    };
  }
  const config = JSON.parse(configStr);
  return {
    scopes: [`${config.auth.clientId}/.default`],
  };
};

export const getMsalInstance = () => {
  if (!msalInstance) {
    const stored = localStorage.getItem("msalConfig");
    const msalConfig = stored ? JSON.parse(stored) : createMsalConfig();
    if (!stored) {
      localStorage.setItem("msalConfig", JSON.stringify(msalConfig));
    }
    msalInstance = new PublicClientApplication(msalConfig);
  }
  return msalInstance;
};

export const getToken = async () => {
  const msalInstance = getMsalInstance();
  const accounts = msalInstance.getAllAccounts();

  const loginRequest = getLoginRequest();

  if (accounts.length === 0) {
    await msalInstance.loginRedirect(loginRequest);
    return null;
  }

  const request = {
    ...loginRequest,
    account: accounts[0],
  };

  try {
    const response = await msalInstance.acquireTokenSilent(request);
    localStorage.setItem("accessToken", response.accessToken);
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect(request);
    } else {
      throw error;
    }
  }
};

export const getTokenFromLocalStorage = () => {
  return localStorage.getItem("accessToken");
};


export const logOut = () => {
  const msalInstance = getMsalInstance();
  const account =
    msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
  localStorage.removeItem("msalConfig");

  // Helpful hints so the IdP ends the right session:
  msalInstance.logoutRedirect({
    account,
    logoutHint: account?.username, // helps Entra pick the right session
    postLogoutRedirectUri: "/", // your public landing
  });
};
